import i18next from "i18next";
import { ItemSlot } from "../item-slot";
import { ItemUI } from "../item-ui";
import { WindowManager } from "../window-manager";
import { COLORS, getMaterialOutlineColor } from "../colors";
import { ModernButton } from "../modern-button";
import { BaseWindow } from "./base-window";
import { playSound } from "../../utils/sound-utils";
import { calcSellPrice, EquipType, Item, ItemType, SlotType, TILE_SIZE } from "../../models/common";

export class InventoryWindow extends BaseWindow {
  private slots: ItemSlot[] = [];
  private selectedItemIdx: number | null = null;
  private actionButtons: Phaser.GameObjects.Container | null = null;
  private windowManager: WindowManager;

  constructor(scene: Phaser.Scene, windowManager: WindowManager, uiLayer?: Phaser.GameObjects.Container) {
    super(scene);
    this.windowManager = windowManager;
    if (uiLayer) this.setUiLayer(uiLayer);
  }

  private refreshBackpackIconOnEquip(item: Item): void {
    if (item.definition.type !== ItemType.Equipment || item.definition.equipType !== EquipType.Back) {
      return;
    }

    if (!window.player) {
      return;
    }

    if (!window.player.data) {
      window.player.data = {} as any;
    }
    if (!window.player.data.equipment) {
      window.player.data.equipment = {};
    }

    window.player.data.equipment[EquipType.Back] = item;
    this.windowManager.updateInventoryIcon();
  }

  show() {
    this.hide();
    if (!this.scene) {
      throw new Error("InventoryWindow.show called without valid scene");
    }
    this.selectedItemIdx = null;
    this.actionButtons = null;

    // Create base window elements (background, title, close button)
    const baseElements = this.createBaseWindow({
      title: i18next.t("ui:inventory"),
      side: "right",
    });

    // Get window bounds for positioning child elements
    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    // Always fetch latest items from player model
    let items: Item[] = [];
    let hasBackpack = false;
    if (window.player && window.player.data && window.player.data.equipment) {
      // Check for backpack in equipment (correct key is 'Back')
      hasBackpack = !!window.player.data.equipment.Back;
    }
    if (window.player && Array.isArray(window.player.data.inventory)) {
      items = [...window.player.data.inventory];
    }

    // Item info
    const itemInfo = this.scene.add
      .text(x + width - this.CONTENT_PADDING_X, y + 7, "", {
        fontSize: "16px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(1, 0);
    itemInfo.setShadow(2, 2, "#000000", 0.7);
    itemInfo.setScrollFactor(0);
    const itemTypeInfo = this.scene.add
      .text(x + width - this.CONTENT_PADDING_X, y + 27, "", {
        fontSize: "12px",
        color: "#ffffff",
        fontFamily: "Anton",
        align: "right",
      })
      .setOrigin(1, 0);
    itemTypeInfo.setScrollFactor(0);

    // Action slots (top row) using ItemSlot
    let actionItems: Item[] = [];
    if (window.player && Array.isArray(window.player.data.actions)) {
      actionItems = [...window.player.data.actions];
    }
    const actionSlotSpacing = 24;
    const actionSlotsY = y + this.CONTENT_PADDING_Y + 24;
    const actionSlots: ItemSlot[] = [];
    const actionSlotSprites: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < 3; i++) {
      const hasItem = !!actionItems[i];
      const itemSlot = new ItemSlot(0, actionSlotsY, hasItem);
      const slotX =
        x + width / 2 + (i - 1.33) * (itemSlot.size + actionSlotSpacing);
      itemSlot.x = slotX;
      itemSlot.setEquipType(EquipType.Action);
      if (hasItem && actionItems[i].definition.type === ItemType.Material) {
        itemSlot.setMaterialOutlineColor(getMaterialOutlineColor(actionItems[i].definition.price));
      }
      if (hasItem && actionItems[i].definition.type === ItemType.Recipe) {
        itemSlot.setBlueprintMode(true);
      }
      // Create circle using ItemSlot method
      const circle = itemSlot.createCircle(this.scene);
      if (hasItem) {
        const item = actionItems[i];
        const sprite = ItemUI.render(
          this.scene,
          item.definition,
          slotX,
          actionSlotsY,
          itemSlot.size,
        );
        if (sprite) {
          sprite.setOrigin(0.5);
          sprite.setScrollFactor(0);
          actionSlotSprites.push(sprite);
        }
        const ammoIndicator = ItemUI.renderAmmoIndicator(
          this.scene,
          item,
          slotX,
          actionSlotsY,
          itemSlot.size,
        );
        if (ammoIndicator) actionSlotSprites.push(ammoIndicator);
        const countBadge = ItemUI.renderCountBadge(this.scene, item, slotX, actionSlotsY, itemSlot.size);
        if (countBadge) actionSlotSprites.push(...countBadge);
      }
      if (itemSlot.rect) {
        itemSlot.rect.on("pointerdown", () => {
          // Play menu-click sound
          playSound(this.scene, "menu-click-sound", null, 0.5);
          // Deselect all inventory slots
          slots.forEach((s) => {
            s.setSelected(false);
            s.applyBorderStyle();
          });
          // Deselect all action slots except this one
          actionSlots.forEach((s, idx) => {
            s.setSelected(idx === i);
            s.applyBorderStyle();
          });
          this.selectedItemIdx = null;
          // Show item name in itemInfo label
          if (hasItem && actionItems[i] && actionItems[i].definition.name) {
            itemInfo.setText(`${actionItems[i].definition.name}`);
            const def = actionItems[i].definition;
            const typeStr = def.type === ItemType.Equipment && def.equipType ? def.equipType : def.type;
            itemTypeInfo.setText(typeStr);
            // Show action buttons for action slot items (will hide USE in base-scene)
            this.showActionSlotButtons(
              this.scene,
              actionItems[i],
              x,
              y,
              width,
              height,
            );
          } else {
            itemInfo.setText("");
            itemTypeInfo.setText("");
            this.hideActionButtons();
          }
        });
      }
      actionSlots.push(itemSlot);
    }

    // Item slots grid (only if backpack equipped)
    const slots: ItemSlot[] = [];
    const itemSprites: Phaser.GameObjects.Image[] = [];
    if (hasBackpack) {
      const gridCols = 4;
      const gridRows = 2;
      const contentWidth = width - this.CONTENT_PADDING_X * 2;
      const contentHeight = height - this.CONTENT_PADDING_Y * 2;
      // Use ItemSlot's standard size
      const itemSlot = new ItemSlot(0, 0);
      const slotSize = itemSlot.size;
      const slotPaddingX =
        (contentWidth - slotSize * gridCols) / (gridCols - 1);
      const slotPaddingY = 8;
      const gridStartX = x + this.CONTENT_PADDING_X;
      const gridStartY = y + this.CONTENT_PADDING_Y * 2 + 64;

      for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
          const slotX = gridStartX + col * (slotSize + slotPaddingX);
          const slotY = gridStartY + row * (slotSize + slotPaddingY);
          const slotIdx = row * gridCols + col;
          const hasItem = !!(items && items[slotIdx]);
          const selected = this.selectedItemIdx === slotIdx;
          const itemSlot = new ItemSlot(slotX, slotY, hasItem);
          itemSlot.setSelected(selected);
          if (hasItem && items[slotIdx].definition.type === ItemType.Material) {
            itemSlot.setMaterialOutlineColor(getMaterialOutlineColor(items[slotIdx].definition.price));
          }
          if (hasItem && items[slotIdx].definition.type === ItemType.Recipe) {
            itemSlot.setBlueprintMode(true);
          }
          // Create rectangle using ItemSlot method
          itemSlot.createRectangle(this.scene);
          if (hasItem) {
            const item = items[slotIdx];
            const sprite = ItemUI.render(
              this.scene,
              item.definition,
              slotX,
              slotY,
              slotSize,
            );
            if (sprite) {
              sprite.setOrigin(0.5);
              sprite.setScrollFactor(0);
              itemSprites.push(sprite);
            }
            const ammoIndicator = ItemUI.renderAmmoIndicator(
              this.scene,
              item,
              slotX,
              slotY,
              slotSize,
            );
            if (ammoIndicator) itemSprites.push(ammoIndicator);
            const recipeBadge = ItemUI.renderRecipeBadge(this.scene, item.definition, slotX, slotY);
            if (recipeBadge) itemSprites.push(...recipeBadge);
            const countBadge = ItemUI.renderCountBadge(this.scene, item, slotX, slotY, slotSize);
            if (countBadge) itemSprites.push(...countBadge);
          }
          if (itemSlot.rect) {
            itemSlot.rect.on("pointerdown", () => {
              // Play menu-click sound
              playSound(this.scene, "menu-click-sound", null, 0.5);
              if (hasItem) {
                this.selectedItemIdx = slotIdx;
                slots.forEach((s, idx) => {
                  s.setSelected(idx === slotIdx);
                  s.applyBorderStyle();
                });

                // Deselect all action slots except this one
                actionSlots.forEach((s) => {
                  s.setSelected(false);
                  s.applyBorderStyle();
                });

                this.showInventorySlotButtons(
                  this.scene,
                  items[slotIdx],
                  x,
                  y,
                  width,
                  height,
                );
                // Update window label to show item name
                if (
                  itemInfo &&
                  items[slotIdx] &&
                  items[slotIdx].definition.name
                ) {
                  itemInfo.setText(`${items[slotIdx].definition.name}`);
                  const def = items[slotIdx].definition;
                  const typeStr = def.type === ItemType.Equipment && def.equipType ? def.equipType : def.type;
                  itemTypeInfo.setText(typeStr);
                }
              } else {
                this.selectedItemIdx = null;
                slots.forEach((s) => {
                  s.setSelected(false);
                  s.applyBorderStyle();
                });
                this.hideActionButtons();
                // Reset window label
                if (itemInfo) {
                  itemInfo.setText("");
                  itemTypeInfo.setText("");
                }
              }
            });
          }
          slots.push(itemSlot);
        }
      }
    }

    // Container
    this.container = this.scene.add.container(0, 0, [
      ...baseElements,
      ...actionSlots.map((s) => s.rect).filter((r) => r !== null),
      ...actionSlotSprites,
      itemInfo,
      itemTypeInfo,
      ...(hasBackpack
        ? slots.map((s) => s.rect).filter((r) => r !== null)
        : []),
      ...(hasBackpack ? itemSprites : []),
    ]);
    this.container.setDepth(100000);
    this.addContainerToParent(this.container);
    this.visible = true;
  }

  private showInventorySlotButtons(
    scene: Phaser.Scene,
    item: Item,
    x: number,
    y: number,
    width: number,
    height: number,
  ) {
    this.hideActionButtons();
    let btnsToShow: string[] = [];
    const btnY = y + height - 8;
    const btnWidth = 96;
    const btnSpacing = 8;

    const isMarketScene = scene.scene && scene.scene.key === "MarketScene";

    
    btnsToShow.push("DROP");

    if (item.definition.type === "Equipment") {
      btnsToShow.push("EQUIP");
    } else if (
      item.definition.type === "Consumable" ||
      item.definition.type === "Ammo"
    ) {
      btnsToShow.push("USE");
    }

    btnsToShow.push("+ACTION");

    // Create buttons first (they will auto-size based on content)
    const buttons: ModernButton[] = [];

    btnsToShow.forEach((label) => {
      let style: "primary" | "secondary" | "success" | "danger" = "primary";

      if (label === "DROP") style = "danger";
      if (label === "USE") style = "success";
      if (label === "STORE") style = "success";
      if (label === "SELL") style = "success";
      if (label === "+ACTION") style = "primary";
      if (label === "EQUIP") style = "success";

      // Map button labels to translation keys
      const labelMap: { [key: string]: string } = {
        "DROP": "ui:buttons.drop",
        "USE": "ui:buttons.use",
        "STORE": "ui:buttons.store",
        "SELL": "ui:buttons.sell",
        "+ACTION": "ui:buttons.addAction",
        "EQUIP": "ui:buttons.equip",
      };

      // Get translated text
      const translatedLabel = i18next.t(labelMap[label]) || label;
      
      // Calculate sell price for SELL button
      let buttonText = translatedLabel;
      if (label === "SELL" && item.definition.price !== undefined) {
        buttonText = `${translatedLabel} ${calcSellPrice(item.definition.price)} c`;
      }

      const button = new ModernButton({
        scene: scene,
        x: 0, // Will be positioned after creation
        y: btnY,
        text: buttonText,
        style: style,
        onClick: () => {
          if (label === "DROP") {
            // Play pick-item sound
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              // Send tile position if in base scene
              const isBaseScene =
                scene.scene && scene.scene.key === "BaseScene";
              if (isBaseScene && window.player) {
                const tileX = Math.floor(window.player.x / TILE_SIZE);
                const tileY = Math.floor(window.player.y / TILE_SIZE);
                (window as any).socket.emit("drop-item", {
                  itemId: item.id,
                  slotType: SlotType.INVENTORY,
                  baseTileX: tileX,
                  baseTileY: tileY,
                });
              } else {
                (window as any).socket.emit("drop-item", {
                  itemId: item.id,
                  slotType: SlotType.INVENTORY,
                });
              }

              this.windowManager.hideLootWindow();
            }
          } else if (label === "STORE") {
            // Play pick-item sound
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              (window as any).socket.emit("put-in-chest", {
                item,
              });
            }
          } else if (label === "SELL") {
            // Play shop sound
            playSound(scene, "shop-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              (window as any).socket.emit("sell-item", {
                itemId: item.id,
              });
            }
          } else if (label === "+ACTION") {
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              (window as any).socket.emit("add-action", {
                itemId: item.id,
              });
            }
          } else if (label === "EQUIP" || label === "USE") {
            // Play pick-item sound for EQUIP
            if (label === "EQUIP") {
              playSound(scene, "pick-item-sound", null, 0.5);
            }
            if (typeof window !== "undefined" && (window as any).socket) {
              if (item.definition.type === "Equipment") {
                this.refreshBackpackIconOnEquip(item);
                (window as any).socket.emit("equip-item", {
                  itemId: item.id,
                  slotType: SlotType.INVENTORY,
                });
              } else {
                // Play heal or bandage sound based on item name
                const itemName = item.definition.name?.toLowerCase() || "";
                if (itemName === "bandage") {
                  playSound(scene, "bandage", null, 0.5);
                } else if (itemName === "health pack" || itemName === "health pill") {
                  playSound(scene, "heal", null, 0.5);
                }
                (window as any).socket.emit("use-item", {
                  itemId: item.id,
                  slotType: SlotType.INVENTORY,
                });
              }
            }
          }
          this.selectedItemIdx = null;
          this.hideActionButtons();
        },
      });

      buttons.push(button);
    });

    // Calculate total width and position buttons
    const totalWidth = buttons.reduce((sum, btn, idx) => {
      return (
        sum +
        (btn as any).buttonWidth +
        (idx < buttons.length - 1 ? btnSpacing : 0)
      );
    }, 0);

    let currentX = x + width / 2 - totalWidth / 2;
    buttons.forEach((btn) => {
      const btnWidth = (btn as any).buttonWidth;
      btn.setX(currentX + btnWidth / 2);
      currentX += btnWidth + btnSpacing;
    });

    // Create container with all buttons (ModernButton extends Container)
    this.actionButtons = scene.add.container(0, 0);
    this.actionButtons.setDepth(100001);
    buttons.forEach((btn) => {
      this.actionButtons!.add(btn);
    });
    if (this.container) {
      this.container.add(this.actionButtons);
    } else {
      this.uiLayer?.add(this.actionButtons);
    }
  }

  // Show '-ACTION' and 'DROP' buttons for action slot items
  private showActionSlotButtons(
    scene: Phaser.Scene,
    item: Item,
    x: number,
    y: number,
    width: number,
    height: number,
  ) {
    this.hideActionButtons();
    const btnY = y + height - 8;
    const btnSpacing = 8;

    const isMarketScene = scene.scene && scene.scene.key === "MarketScene";

    const merchantWindowVisible =
      this.windowManager &&
      typeof this.windowManager.isMerchantWindowVisible === "function" &&
      this.windowManager.isMerchantWindowVisible();
    const btnsToShow = [];

    // Determine which buttons to show
    if ((isMarketScene && merchantWindowVisible)) {
      btnsToShow.push("SELL");
    } else {
      // Only show DROP if storage window is not visible
      btnsToShow.push("DROP");
      
      if (window.player.data.equipment.Back) {
        btnsToShow.push("-ACTION");
      }
      if (item.definition.type === "Equipment") {
        btnsToShow.push("EQUIP");
      }
      // Only show USE if not in base scene and item is Consumable or Ammo
      if (
        item.definition.type === "Consumable" ||
        item.definition.type === "Ammo"
      ) {
        btnsToShow.push("USE");
      }
    }

    const buttons: ModernButton[] = [];
    btnsToShow.forEach((label) => {
      let style: "primary" | "secondary" | "success" | "danger" = "success";

      if (label === "DROP") style = "danger";
      if (label === "STORE") style = "success";
      if (label === "SELL") style = "success";
      if (label === "-ACTION") style = "primary";
      if (label === "+ACTION") style = "primary";
      if (label === "EQUIP") style = "success";
      if (label === "USE") style = "success";

      // Map button labels to translation keys
      const labelMap: { [key: string]: string } = {
        "DROP": "ui:buttons.drop",
        "USE": "ui:buttons.use",
        "STORE": "ui:buttons.store",
        "SELL": "ui:buttons.sell",
        "-ACTION": "ui:buttons.removeAction",
        "+ACTION": "ui:buttons.addAction",
        "EQUIP": "ui:buttons.equip",
      };

      // Get translated text
      const translatedLabel = i18next.t(labelMap[label]) || label;

      // Calculate sell price for SELL button (half of item price)
      let buttonText = translatedLabel;
      if (label === "SELL" && item.definition.price !== undefined) {
        buttonText = `${translatedLabel} ${calcSellPrice(item.definition.price)} c`;
      }

      const button = new ModernButton({
        scene: scene,
        x: 0, // Will be positioned after creation
        y: btnY,
        text: buttonText,
        style: style,
        onClick: () => {
          if (label === "DROP") {
            // Play pick-item sound
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              // Send tile position if in base scene
              const isBaseScene =
                scene.scene && scene.scene.key === "BaseScene";
              let tilePos = undefined;
              if (isBaseScene && window.player) {
                tilePos = {
                  x: Math.floor(window.player.x / TILE_SIZE),
                  y: Math.floor(window.player.y / TILE_SIZE),
                };
                if (window.windowManager?.isLootWindowVisible()) {
                  tilePos = window.windowManager
                    .getLootWindow()
                    .getCurrentLootBox();
                }
              }
              (window as any).socket.emit("drop-item", {
                itemId: item.id,
                slotType: SlotType.ACTION,
                baseTileX: tilePos ? tilePos.x : undefined,
                baseTileY: tilePos ? tilePos.y : undefined,
              });
            }
          } else if (label === "STORE") {
            // Play pick-item sound
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              (window as any).socket.emit("put-in-chest", {
                item,
              });
            }
          } else if (label === "SELL") {
            // Play shop sound
            playSound(scene, "shop-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              (window as any).socket.emit("sell-item", {
                itemId: item.id,
              });
            }
          } else if (label === "-ACTION") {
            // Play pick-item sound for unequip
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              (window as any).socket.emit("unequip-item", {
                itemId: item.id,
                slotType: SlotType.ACTION,
              });
            }
          } else if (label === "USE") {
            if (typeof window !== "undefined" && (window as any).socket) {
              // Play heal or bandage sound based on item name
              const itemName = item.definition.name?.toLowerCase() || "";
              if (itemName === "bandage") {
                playSound(scene, "bandage", null, 0.5);
              } else if (itemName === "health pack" || itemName === "health pill") {
                playSound(scene, "heal", null, 0.5);
              }
              (window as any).socket.emit("use-item", {
                itemId: item.id,
                slotType: SlotType.ACTION,
              });
            }
          } else if (label === "EQUIP") {
            // Play pick-item sound
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              this.refreshBackpackIconOnEquip(item);
              (window as any).socket.emit("equip-item", {
                itemId: item.id,
                slotType: SlotType.ACTION,
              });
            }
          }
          this.hideActionButtons();
        },
      });

      buttons.push(button);
    });

    // Calculate total width and position buttons
    const totalWidth = buttons.reduce(
      (sum: number, btn: ModernButton, idx: number) => {
        return (
          sum + btn.buttonWidth + (idx < buttons.length - 1 ? btnSpacing : 0)
        );
      },
      0,
    );

    let currentX: number = x + width / 2 - totalWidth / 2;
    buttons.forEach((btn: ModernButton) => {
      const btnWidth = btn.buttonWidth;
      btn.setX(currentX + btnWidth / 2);
      currentX += btnWidth + btnSpacing;
    });

    // Create container with all buttons (ModernButton extends Container)
    this.actionButtons = scene.add.container(0, 0);
    this.actionButtons.setDepth(100001);
    buttons.forEach((btn: ModernButton) => {
      this.actionButtons!.add(btn);
    });
    if (this.container) {
      this.container.add(this.actionButtons);
    } else {
      this.uiLayer?.add(this.actionButtons);
    }
  }

  private hideActionButtons() {
    if (this.actionButtons) {
      this.actionButtons.destroy();
      this.actionButtons = null;
    }
  }

  hide() {
    this.hideActionButtons();
    this.slots = [];
    super.hide();
  }
}
